<template>
  <div class="payment-widget">
    <div id="dropin-placeholder">
      <callout v-if="loading" type="info" :closable="false">
        <div>Loading payment options...</div>
      </callout>

      <callout v-if="error" type="error" :closable="false">
        <div>{{ error }}</div>
      </callout>
    </div>
    <div id="dropin-container"></div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import dropIn from 'braintree-web-drop-in'
import Callout from '@/components/notifications/callout.vue'

/**
 * Braintree payment widget
 *
 * @see https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html
 * - Supports 3d secure out of the box
 *
 * Emits the following events
 * - payment-drop-in-loaded
 * - three-d-secure-customer-canceled
 * - payment-method-requestable
 *
 * For recurring, we hide some UI elements related to vaulting as it is pretty
 * clunky and for 3ds, would instant vault on client side, even if cancelling 3ds.
 *
 * In short, after hiding elements, recurring works like non-recurring drop in UI.
 */
export default defineComponent({
  name: 'BraintreeWebDropIn',
  components: {
    Callout,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    total: {
      type: String,
      default: null,
    },
    paymentFlowType: {
      type: String,
      default: 'checkout',
      validator: function (value) {
        // The value must match one of these strings
        return ['checkout', 'recurring'].indexOf(value) !== -1
      },
    },
  },
  emits: [
    'payment-drop-in-loaded',
    'three-d-secure-customer-canceled',
    'payment-method-requestable',
  ],
  data() {
    return {
      braintreeInstance: null,
      loading: true,
      error: '',
    }
  },
  computed: {
    options() {
      if (this.paymentFlowType === 'checkout') {
        return {
          paypal: {
            flow: 'checkout',
            commit: true,
            amount: `${this.total}`,
            currency: 'USD',
          },
          applePay: {
            displayName: 'Fitness Blender',
            paymentRequest: {
              total: {
                label: 'Fitness Blender',
                amount: `${this.total}`,
              },
            },
          },

          // googlePay: {
          //   googlePayVersion: 2,
          //   merchantId: 'merchant-id-from-google',
          //   transactionInfo: {
          //     totalPriceStatus: 'FINAL',
          //     totalPrice: '{{ number_format($user_purchase->total,2) }}',
          //     currencyCode: 'USD'
          //   },
        }
      }

      // recurring
      return {
        // defaults
        paypal: {
          flow: 'vault',
        },
        applePay: {
          displayName: 'Fitness Blender',
          paymentRequest: {
            total: {
              label: 'Fitness Blender',
              amount: `${this.total}`,
            },
          },
        },
      }
    },
  },

  watch: {
    // update the braintree instances
    total(newTotal, prevTotal) {
      if (this.braintreeInstance && newTotal !== prevTotal) {
        // update totals if checkout
        if (this.paymentFlowType === 'checkout') {
          this.braintreeInstance.updateConfiguration('paypal', 'amount', `${newTotal}`)
          this.braintreeInstance.updateConfiguration('applePay', 'paymentRequest', {
            total: {
              label: 'Fitness Blender',
              amount: `${newTotal}`,
            },
          })
          // TODO: googlepay
        }

        // updates if recurring
        if (this.paymentFlowType === 'recurring') {
          this.braintreeInstance.updateConfiguration('applePay', 'paymentRequest', {
            total: {
              label: 'Fitness Blender',
              amount: `${newTotal}`,
            },
          })
        }
      }
    },
  },

  /* load braintree after dom loads */
  mounted() {
    this.createBraintreeFrame()
  },

  /* remove braintree if SPA */
  beforeUnmount() {
    if (this.braintreeInstance) {
      this.braintreeInstance.teardown()
    }
  },
  methods: {
    // un-hide payment method container for single transactions
    // TODO: always test this when upgrading braintree
    fbCustomizeSingleUI() {
      // un-hide
      const methodContainer = document.querySelector('[data-braintree-id="methods-container"]')

      if (methodContainer) {
        methodContainer.style.display = 'block'
      }
    },

    // helper to hide saved payment methods and forcing user to select a new payment options
    // TODO: always test this when upgrading braintree
    fbCustomizeRecurringUI() {
      // nothing should be selected, so hide all
      this.fbHideAllMethods()

      const lowerTitle = document.querySelector('[data-braintree-id="other-ways-to-pay"]')
      const choseAnotherWayBtn = document.querySelector('[data-braintree-id="toggle"]')

      if (lowerTitle) {
        lowerTitle.innerHTML = 'Choose a way to pay'
      }

      if (choseAnotherWayBtn) {
        choseAnotherWayBtn.click()
      }
    },

    // helper to hide the entire container for payment methods, including active
    // TODO: always test this when upgrading braintree
    fbHideAllMethods() {
      const methodLabel = document.querySelector('[data-braintree-id="methods-label"]')
      const methodContainer = document.querySelector('[data-braintree-id="methods-container"]')

      if (methodContainer) {
        methodContainer.style.display = 'none'
      }

      if (methodLabel) {
        methodLabel.style.display = 'none'
      }
    },

    // helper method to un-hide the active payment method, while hiding all inactive ones
    // this is tricky as we need to re-show the container (that was hidden)
    // but then hide all individual payment methods within the container
    // except for the active / selected one
    // TODO: always test this when upgrading braintree
    fbShowActive() {
      // selectors
      const methodLabel = document.querySelector('[data-braintree-id="methods-label"]')
      const methodContainer = document.querySelector('[data-braintree-id="methods-container"]')
      const showSelected = document.querySelector('.braintree-method--active')
      const allMethods = document.querySelectorAll('.braintree-method')

      if (showSelected) {
        // hide none-selected payment methods
        if (allMethods) {
          allMethods.forEach((el) => {
            el.style.display = 'none'
          })
        }

        showSelected.removeAttribute('style')

        // re-show container
        if (methodContainer) {
          methodContainer.style.display = 'block'
        }

        if (methodLabel) {
          methodLabel.removeAttribute('style')
        }
      }
    },

    /**
     * Load the braintree drop in UI
     */
    createBraintreeFrame() {
      dropIn.create(
        {
          authorization: this.token,
          container: '#dropin-container',
          // TODO: disabled threeDSecure
          threeDSecure: true, // 3d secure enabled 2020-02-05
          vaultManager: false, // if customer id used with token, disable deleting payment methods
          showDefaultPaymentMethodFirst: true,
          preselectVaultedPaymentMethod: false, // never preselect a payment method
          ...this.options,
        },
        (createdErr, instance) => {
          this.loading = false

          if (createdErr) {
            /*
              An error in the create call is likely due to
              incorrect configuration values or network issues.
             */
            console.error(createdErr)
            this.error =
              'Unable to load the payment options.  Please refresh the page, or contact support if this happens again.'
            return
          }

          // if recurring, hide vaulted items onload, otherwise show
          if (this.paymentFlowType === 'recurring') {
            this.fbCustomizeRecurringUI()
          } else {
            this.fbCustomizeSingleUI()
          }

          // pass braintree instance up to parent component
          // add custom methods
          this.braintreeInstance = instance

          if (this.paymentFlowType === 'recurring') {
            // expose helper to reset ui
            this.braintreeInstance.fbCustomizeRecurringUI = this.fbCustomizeRecurringUI

            // show active payment method once it is selectable
            this.braintreeInstance.on('paymentMethodRequestable', (e) => {
              if (e.paymentMethodIsSelected) {
                this.fbShowActive()
              }

              // also send to parent component
              this.$emit('payment-method-requestable', e)
            })
          }

          this.$emit('payment-drop-in-loaded', this.braintreeInstance)

          // attach event listener to 3d secure cancel event
          this.braintreeInstance.on('3ds:customer-canceled', () => {
            // reset UI and hide payment methods
            this.braintreeInstance.clearSelectedPaymentMethod()

            // next tick just because the clear method above is outside of
            // vue.... when it comes to race conditions, when in doubt setTimeout
            this.$nextTick(() => {
              if (this.paymentFlowType === 'recurring') {
                this.fbCustomizeRecurringUI()
              }

              this.$emit('three-d-secure-customer-canceled')
            })
          })
          // end customer cancel

          // TODO: old event, can delete after 3ds launch
          // attach event listener to 3d secure cancel event
          // this is not exposed directly, but can be accessed on the instance
          // @see https://braintree.github.io/braintree-web/current/ThreeDSecure.html#event:customer-canceled
          // if (
          //   this.braintreeInstance &&
          //   this.braintreeInstance._threeDSecure &&
          //   this.braintreeInstance._threeDSecure._instance
          // ) {
          //   this.braintreeInstance._threeDSecure._instance.on('customer-canceled', () => {
          //     // reset UI and hide payment methods
          //     this.braintreeInstance.clearSelectedPaymentMethod()
          //
          //     // next tick just because the clear method above is outside of
          //     // vue.... when it comes to race conditions, when in doubt setTimeout
          //     this.$nextTick(() => {
          //       if (this.paymentFlowType === 'recurring') {
          //         this.fbCustomizeRecurringUI()
          //       }
          //
          //       this.$emit('three-d-secure-customer-canceled')
          //     })
          //   })
          // }
          // end customer cancel
        },
      )
    },
  },
})
</script>
